import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { errors, paxRoute, type RouteAuth, serviceRefusals, UserId } from "../../common";

const basemapRoomAuth: RouteAuth = {
  kind: "service",
  callers: { "asset-editor": "basemaps:admit" },
};

export const BasemapEditorAdmission = z.discriminatedUnion("status", [
  z.strictObject({
    status: z.literal("admitted"),
    userId: UserId,
    name: z.string().min(1).meta({ description: "Their display name, for everyone in the draft." }),
  }),
  z
    .strictObject({ status: z.literal("signedOut") })
    .meta({ description: "The cookie carries no session." }),
  z.strictObject({ status: z.literal("banned") }).meta({ description: "The account is banned." }),
  z.strictObject({ status: z.literal("noDraft") }).meta({
    description:
      "The draft doesn't exist, isn't a draft any more, or its asset isn't theirs or one they collaborate on.",
  }),
]);
export type BasemapEditorAdmission = z.infer<typeof BasemapEditorAdmission>;

export const admitBasemapEditor = oc
  .meta(
    paxRoute({ auth: basemapRoomAuth, minimumTier: "suspicious", cost: "read" }),
    openapi({
      method: "POST",
      path: "/internal/basemaps/v1alpha1/{id}/admission",
      operationId: "internal.basemaps.admission",
      summary: "Check whether a signed-in account may open a basemap draft.",
      description:
        "Reads the account from the browser's sign-in cookie, which the asset editor passes on, and checks that it isn't banned and that it owns or collaborates on the draft's asset.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals })
  .input(
    z.object({
      params: z.strictObject({ id: z.uuid().meta({ description: "The draft's version id." }) }),
      headers: z.object({ "x-pax-cookie": z.string().optional() }),
    }),
  )
  .output(BasemapEditorAdmission);
